import { Geom, intersection, Pair, Ring } from 'polygon-clipping';
import Konva from 'konva';
import { INVALID_AREA_THRESHOLD } from '../constant';

export type ValidationResult = {
  valid: boolean;
  reason: string;
};

function isIntersectionAreaWithInThreshold(smallPolygonPoints: number[], largePolygonPoints: number[], threshold = 0.98): boolean {
	const smallPolygon = [toRing(smallPolygonPoints)];
	const targetPolygon = [toRing(largePolygonPoints)];

	const intersectionResult = intersection(smallPolygon as Geom, targetPolygon as Geom);

	if (!intersectionResult?.[0]?.[0]?.length) return false;
	const intersectionArea = polygonArea(intersectionResult[0][0]);
	return intersectionArea > threshold;
}

function roundPoint([x, y]: number[], precision = 3): Pair {
	const factor = Math.pow(10, precision);
	return [x,y];
	return [
		Math.round(x * factor) / factor,
		Math.round(y * factor) / factor,
	];
}

function toRing(flat: number[], precision = 3): Ring {
	if (flat.length < 4) return [];

	const ring: Ring = [];

	for (let i = 0; i < flat.length; i += 2) {
		const p = roundPoint([flat[i], flat[i + 1]], precision);

		// Skip if the previous point is identical
		const last = ring[ring.length - 1];
		if (!last || last[0] !== p[0] || last[1] !== p[1]) {
			ring.push(p);
		}
	}

	// Ensure closure
	if (ring.length > 2) {
		const first = ring[0];
		const last = ring[ring.length - 1];
		if (first[0] !== last[0] || first[1] !== last[1]) {
			ring.push([...first]);
		}
	}

	return ring;
}

// Shoelace formula for area
function polygonArea(ring: Ring): number {
	let area = 0;
	const n = ring.length;

	const limit =
		ring[0][0] === ring[n - 1][0] && ring[0][1] === ring[n - 1][1]
			? n - 1
			: n;

	for (let i = 0; i < limit; i++) {
		const j = (i + 1) % limit;
		const term = ring[i][0] * ring[j][1] - ring[j][0] * ring[i][1];
		// console.log(`(${i},${j}) term =`, term);
		area += term;
	}

	// console.log('Raw area sum:', area);

	return Math.round(Math.abs(area / 2));
}

// Duplicate or collinear consecutive points
function hasDuplicateOrCollinear(ring: Ring): boolean {
	const n = ring.length;
	for (let i = 0; i < n; i++) {
		const p1 = ring[i];
		const p2 = ring[(i + 1) % n];
		const p3 = ring[(i + 2) % n];

		// Duplicate
		if (p1[0] === p2[0] && p1[1] === p2[1]) {
			return true;
		}

		// Collinear check (triangle area = 0)
		const area =
      (p2[0] - p1[0]) * (p3[1] - p1[1]) -
      (p3[0] - p1[0]) * (p2[1] - p1[1]);
		if (area === 0) {
			return true;
		}
	}
	return false;
}

// Connectivity check (graph traversal on edges)
function isDisconnected(ring: Ring): boolean {
	const n = ring.length;
	if (n < 3) return true;

	const adj: Record<number, number[]> = {};
	for (let i = 0; i < n; i++) {
		const a = i;
		const b = (i + 1) % n;
		adj[a] = (adj[a] || []).concat(b);
		adj[b] = (adj[b] || []).concat(a);
	}

	const visited = new Set<number>();
	const stack = [0];
	while (stack.length) {
		const node = stack.pop()!;
		if (!visited.has(node)) {
			visited.add(node);
			(adj[node] || []).forEach((nei) => {
				if (!visited.has(nei)) stack.push(nei);
			});
		}
	}

	return visited.size !== n;
}

function ccw(a: Pair, b: Pair, c: Pair): boolean {
	return (c[1] - a[1]) * (b[0] - a[0]) > (b[1] - a[1]) * (c[0] - a[0]);
}

function segmentsIntersect(p1: Pair, p2: Pair, p3: Pair, p4: Pair): boolean {
	return (
		ccw(p1, p3, p4) !== ccw(p2, p3, p4) &&
    ccw(p1, p2, p3) !== ccw(p1, p2, p4)
	);
}

function isSelfIntersecting(ring: Ring): boolean {
	const n = ring.length;
	const limit =
		ring[0][0] === ring[n - 1][0] && ring[0][1] === ring[n - 1][1]
			? n - 1
			: n;

	for (let i = 0; i < limit; i++) {
		const a1 = ring[i];
		const a2 = ring[(i + 1) % limit];

		for (let j = i + 1; j < limit; j++) {
			const b1 = ring[j];
			const b2 = ring[(j + 1) % limit];

			// skip adjacent edges (they share a point)
			if (i === j || (i + 1) % limit === j || (j + 1) % limit === i) continue;

			if (segmentsIntersect(a1, a2, b1, b2)) {
				return true;
			}
		}
	}
	return false;
}

// --- Main validator --- //
function validatePolygon(flatPoints: number[]): ValidationResult {
	const ring = toRing(flatPoints);

	if(!ring.length) return { valid: true, reason: 'No points' };

	if (ring.length < 3) {
		return { valid: false, reason: 'Too few points' };
	}

	// // console.log(ring,polygonArea(ring));

	if (Math.abs(polygonArea(ring)) <= 1) {
		return { valid: false, reason: 'Zero-area polygon' };
	}

	// if (hasDuplicateOrCollinear(ring)) {
	// 	return { valid: false, reason: 'Duplicate/collinear points' };
	// }

	if (isDisconnected(ring)) {
		return { valid: false, reason: 'Disconnected geometry' };
	}

	if (isSelfIntersecting(ring)) {
		return { valid: false, reason: 'Self-intersecting' };
	}

	return { valid: true, reason: 'Valid polygon' };
}

type BoundingBox = { minX: number; minY: number; maxX: number; maxY: number };

function getBoundingBox(ring: Ring): BoundingBox {
	let minX = Infinity, minY = Infinity;
	let maxX = -Infinity, maxY = -Infinity;

	for (const [x, y] of ring) {
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	}

	return { minX, minY, maxX, maxY };
}

function boxesOverlap(a: BoundingBox, b: BoundingBox): boolean {
	return !(
		a.maxX < b.minX || // a is left of b
    a.minX > b.maxX || // a is right of b
    a.maxY < b.minY || // a is above b
    a.minY > b.maxY // a is below b
	);
}

function validateDuringDrag(
	currentSegmentId: string,
	currentLine: Konva.Line,
	allFacets: editableFacet[],
	konvaRef: React.RefObject<Konva.Stage>,

) {
	const points = currentLine.points();
	const validation = validatePolygon(points);

	if(!validation.valid) return false;

	// convert current to ring for bbox check
	const ringA = toRing(points);
	const boxA = getBoundingBox(ringA);

	let intersectsOther = false;
	for (const facet of allFacets) {
		if (facet.segmentId === currentSegmentId) continue;

		const otherLine = konvaRef.current?.findOne(`#editable-${facet.segmentId}`) as Konva.Line;
		if (!otherLine) continue;

		const ringB = toRing(otherLine.points());
		if (ringB.length < 3) continue;

		// bounding box pre-check
		if (!boxesOverlap(boxA, getBoundingBox(ringB))) continue;

		// real polygon intersection
		if (isIntersectionAreaWithInThreshold(points, otherLine.points(), INVALID_AREA_THRESHOLD)) {
			intersectsOther = true;
			break;
		}
	}
	
	return !intersectsOther;
}

export {
	validatePolygon,
	isIntersectionAreaWithInThreshold,
	validateDuringDrag
};